import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Chip,
  Autocomplete,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'select' | 'multiselect' | 'checkbox' | 'switch' | 'textarea' | 'autocomplete';
  required?: boolean;
  placeholder?: string;
  options?: Array<{ value: string | number; label: string }>;
  validation?: {
    min?: number;
    max?: number;
    pattern?: RegExp;
    message?: string;
  };
  grid?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  helperText?: string;
}

export interface FormBuilderProps {
  fields: FormField[];
  initialData?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => void | Promise<void>;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  title?: string;
  loading?: boolean;
  error?: string;
  success?: string;
  validation?: (data: Record<string, any>) => Record<string, string>;
}

export function FormBuilder({
  fields,
  initialData = {},
  onSubmit,
  onCancel,
  submitText = 'Kaydet',
  cancelText = 'İptal',
  title,
  loading = false,
  error,
  success,
  validation,
}: FormBuilderProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const validateField = (name: string, value: any): string => {
    const field = fields.find(f => f.name === name);
    if (!field) return '';

    // Required validation
    if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return `${field.label} gereklidir`;
    }

    // Type-specific validation
    if (value) {
      if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Geçerli bir email adresi giriniz';
      }

      if (field.type === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          return 'Geçerli bir sayı giriniz';
        }
        if (field.validation?.min && numValue < field.validation.min) {
          return `Minimum değer ${field.validation.min} olmalıdır`;
        }
        if (field.validation?.max && numValue > field.validation.max) {
          return `Maksimum değer ${field.validation.max} olmalıdır`;
        }
      }

      if (field.validation?.pattern && !field.validation.pattern.test(value)) {
        return field.validation.message || 'Geçersiz format';
      }
    }

    return '';
  };

  const handleFieldChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    fields.forEach(field => {
      const error = validateField(field.name, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    // Custom validation
    if (validation) {
      const customErrors = validation(formData);
      Object.assign(newErrors, customErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        await onSubmit(formData);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name];
    const error = errors[field.name];
    const showPasswordForField = showPassword[field.name];

    const commonProps = {
      fullWidth: true,
      label: field.label,
      placeholder: field.placeholder,
      required: field.required,
      disabled: field.disabled || loading,
      error: !!error,
      helperText: error || field.helperText,
      value: value || '',
      onChange: (e: any) => handleFieldChange(field.name, e.target.value),
    };

    switch (field.type) {
      case 'password':
        return (
          <TextField
            {...commonProps}
            type={showPasswordForField ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(prev => ({ ...prev, [field.name]: !showPasswordForField }))}
                    edge="end"
                  >
                    {showPasswordForField ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth error={!!error} disabled={field.disabled || loading}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              label={field.label}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'multiselect':
        return (
          <Autocomplete
            multiple
            options={field.options || []}
            getOptionLabel={(option) => option.label}
            value={value || []}
            onChange={(_, newValue) => handleFieldChange(field.name, newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                error={!!error}
                helperText={error || field.helperText}
                required={field.required}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.label}
                  {...getTagProps({ index })}
                  key={option.value}
                />
              ))
            }
          />
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                disabled={field.disabled || loading}
              />
            }
            label={field.label}
          />
        );

      case 'switch':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value || false}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                disabled={field.disabled || loading}
              />
            }
            label={field.label}
          />
        );

      case 'textarea':
        return (
          <TextField
            {...commonProps}
            multiline
            rows={field.rows || 4}
          />
        );

      case 'autocomplete':
        return (
          <Autocomplete
            options={field.options || []}
            getOptionLabel={(option) => option.label}
            value={value ? field.options?.find(opt => opt.value === value) || null : null}
            onChange={(_, newValue) => handleFieldChange(field.name, newValue?.value || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                error={!!error}
                helperText={error || field.helperText}
                required={field.required}
              />
            )}
          />
        );

      default:
        return (
          <TextField
            {...commonProps}
            type={field.type}
          />
        );
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
          {title}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {fields.map((field) => (
          <Grid
            item
            xs={field.grid?.xs || 12}
            sm={field.grid?.sm || 6}
            md={field.grid?.md || 4}
            lg={field.grid?.lg || 3}
            key={field.name}
          >
            {renderField(field)}
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {submitText}
        </Button>
      </Box>
    </Box>
  );
} 